import type { FileDTO } from '../media/media'
import {
  netClient,
  type CommonResponse,
  DETECT_FACE,
  PORTRAIT_CREATE_ORDER_WECHATJS,
  PORTRAIT_CREATE_ORDER_WECHATQR,
  PORTRAIT_ENHANCE_PLANS,
  PORTRAIT_CREATE_TASK,
  PORTRAIT_TASK_NODE_DETAIL,
  PORTRAIT_LIST_MATERIAL,
  PORTRAIT_PHOTO_BEAUTIFY,
  PORTRAIT_SELECT_NODE_AND_ORDER,
  PORTRAIT_HOME,
  PORTRAIT_PAY_BY_COIN,
  PORTRAIT_PAY_BY_MEMBER,
  PORTRAIT_TASK_DETAIL,
  PORTRAIT_TASKS_ME,
  PORTRAIT_TASK_DELETE,
  PORTRAIT_TASK_RETRY,
  PORTRAIT_TASK_SHARE,
  PORTRAIT_TASKS_OPEN,
  PORTRAIT_DETECT_FACES,
  PORTRAIT_ADVICE,
  PORTRAIT_SET_TRANSPARENT,
  PORTRAIT_MERGE_BACKGROUND,
  PORTRAIT_CAPTURE,
  PORTRAIT_MAKE_HD,
  PORTRAIT_COLORIZE,
  PORTRAIT_ADJUST_GESTURE,
  PORTRAIT_ENHANCE_PLANS_SCENE,
  PORTRAIT_ENHANCE_LAYOUT,
  PORTRAIT_SELECT_NODE
} from '@/apis/api'
import type { CreateOrderNativeResponse, CreateWechatOrderResponse } from '../payment'
import type { UserMinDTO } from '@/composables/user/users'
import { useLocalStorage } from '@vueuse/core'
import { type Ref } from 'vue'
import type { CoinAccountDTO } from '@/composables/coin/coin'
import { inMiniprograme, isIOS } from '@/utils/util'
export interface UploadFileDetectFaceResponse {
  fileId: string
  file: FileDTO
  faceCount: number // 人物数量
}
export interface UploadFileDetectFaceRequest {
  file: Blob | null
  trans: boolean
  fileName: string
}

export function uploadFileDetectFace(uploadCommand: UploadFileDetectFaceRequest) {
  const formData = new FormData()
  if (uploadCommand.file != null) {
    formData.append('file', uploadCommand.file, uploadCommand.fileName)
  }
  formData.append('trans', String(uploadCommand.trans))
  return netClient.formPut<CommonResponse<UploadFileDetectFaceResponse>>(DETECT_FACE, formData)
}

export const backgroundListCached: Ref<BackgroundListCached> = useLocalStorage('portrait_background_list', {
  ttl: 0,
  background: [],
  hair: [],
  cloth: []
})
export interface BackgroundListCached {
  ttl: number
  background: PhotoEnhancePlanDTO[]
  hair: PhotoMaterialDTO[]
  cloth: PhotoMaterialDTO[]
}

export const TTL = 60 * 60 * 1000 //默认一小时

export const loadingTips = [
  '正在去除背景...',
  '正在深度解析面部轮廓...',
  '正在智能降噪处理提升清晰度...',
  '正在修复服装...',
  '正在修复人物姿态...',
  '正在精准复现五官立体细节...',
  '正在更换背景...',
  '正在调整柔和庄重光影氛围...',
  '即将生成高保真影像...'
]

export interface PortraitOrderNativeCommand {
  PhotoEnhanceTaskId: string
}

//微信
export interface OrderJsCommand {
  PhotoEnhanceTaskId: string
  miniApp: boolean
}

export function createPortraitJsWechatOrder(orderCommand: OrderJsCommand) {
  return netClient.request<CommonResponse<CreateWechatOrderResponse>>(
    PORTRAIT_CREATE_ORDER_WECHATJS,
    'post',
    orderCommand
  )
}

export function createPortraitWechatOrderQR(orderCommand: PortraitOrderNativeCommand) {
  return netClient.request<CommonResponse<CreateOrderNativeResponse>>(
    PORTRAIT_CREATE_ORDER_WECHATQR,
    'post',
    orderCommand
  )
}

export interface PhotoEnhancePlanDTO {
  id: string
  planName: string
  basePromptId: string
  backupPromptId: string
  showcaseUrl: string
  bgType: string
  defaultRatio: string
  defaultResolution: string
  defaultSize: string
  bgColor: string
  bgStartColor: string
  bgEndColor: string
  bgImg: string
  spec: string
  editorId: string
  grayScale: boolean
  gender: string
  method: string // 方式 api / llm / native 展示忽略
  price: number
  memberPrice: number
  coin: number
  memberCoin: number
  implementKey: string
  createdAt: number
}

// 方案id + 原始照片url = 创建照片精修任务 ↓

// 照片精修任务
export interface PhotoEnhanceTaskDTO {
  id: string
  enhancePlanId: string
  price: number
  cost: number
  coins: number
  member: boolean
  bonus: boolean
  originalPrice: number
  originalCoin: number
  creatorId: string
  creator: UserMinDTO
  paid: boolean
  open: boolean
  leftTimes: number
  sourceFileId: string
  sourceFile: FileDTO
  targetFileId: string
  targetFile: FileDTO
  lifeId: string
  altarId: string
  nodes: Array<PhotoEnhanceNodeDTO>
  test: string // 默认 a 分场景 b
  scene: string // 场景值，默认为'general'是指遗像一键美化
}

// 照片精修任务节点
export interface PhotoEnhanceNodeDTO {
  id: string
  enhancePlanId: string
  promptId: string
  slotMap: string
  spec: string
  sourceFileId: string
  sourceFile: FileDTO
  sourceUrls: Array<String> // 修改为数组
  targetFileId: string
  targetFile: FileDTO
  targetUrl: string
  userId: string
  taskId: string
  cost: number
  retryTimes: number
  finalPrompt: string
  step: number
  aiTaskId: string
  selected: boolean
}

export interface PhotoEnhancePlanResponse {
  scene: string
  plans: Array<PhotoEnhancePlanDTO>
}

//scene - 传固定值：'pe_scene_general'
export function getPhotoEnhancePlans() {
  return netClient.request<CommonResponse<PhotoEnhancePlanResponse>>(PORTRAIT_ENHANCE_PLANS, 'get', {
    scene: 'pe_scene_general',
    iosMiniapp: isIOS() && inMiniprograme.value
  })
}

export interface CreatePhotoEnhancementCommand {
  planId: string
  fileId: string
  imageUrl: string
  lifeId: string | null
  altarId: string | null
}

export function createPhotoEnhancement(enhanceCommand: CreatePhotoEnhancementCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_CREATE_TASK, 'post', enhanceCommand)
}

export interface PhotoEnhanceTaskCheckResponse {
  aiTaskId: string
  node: PhotoEnhanceNodeDTO
  status: string // 'success', 'fail', 'progress' 成功，失败，正在处理
  msg: string // 错误信息
}
export function getPhotoEnhanceTaskNodeDetail(nodeId: string) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskCheckResponse>>(
    PORTRAIT_TASK_NODE_DETAIL.replace('{nodeId}', nodeId),
    'get',
    {}
  )
}

export interface ListPhotoMaterialResponse {
  photoMaterials: Array<PhotoMaterialDTO>
}

export interface PhotoMaterialDTO {
  id: string
  materialName: string
  category: string
  sourceFileId: string
  sourceFile: FileDTO
  showcaseFileId: string
  showcaseFile: FileDTO
  creatorId: string
  rankNo: number
  startAge: number
  endAge: number
  gender: string
  createdAt: number
  updatedAt: number
}

/**
 * Request Params:
category - string   of  'cloth' | 'hair'  
 */
export function listPhotoMaterial(category: string) {
  return netClient.request<CommonResponse<ListPhotoMaterialResponse>>(PORTRAIT_LIST_MATERIAL, 'get', { category })
}

export interface SelectedPhotoMaterialDTO {
  hair: PhotoMaterialDTO | null
  cloth: PhotoMaterialDTO | null
  beauty: Array<string>
}

export interface BeautifyPhotoCommand {
  nodeId: string
  clothMaterialId: string // 衣服素材id
  hairMaterialId: string // 发型素材id
  beautyParams: Array<String> // 美容参数key
  ageDiff: number // 年龄变化diff，如 +5 / -20
}

export function beautifyPhoto(beautifyCommand: BeautifyPhotoCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_PHOTO_BEAUTIFY, 'post', beautifyCommand)
}

// request
export interface SelectNodeAndOrderCommand {
  nodeId: string
  inMiniProgram: boolean
  iosMiniapp: boolean
}

// response
export interface PhotoOutputFileDTO {
  taskId: string
  outputFile: Record<string, string> // { "1": "https://xxxx.png", "8": "xxx", "origin": "" } 从1寸就是1，一直到24寸， origin-原图
}

export interface SelectAndOrderResponse {
  price: number // 价格分
  originalPrice: number // 原价
  coinAccount: CoinAccountDTO // coin
  coin: number // 用户思念豆价格
  originalCoin: number // 思念豆原价
  member: boolean // 是否会员
  discountRate: number // 折扣度， 如会员是80（8折），非会员是100
  outputFile: PhotoOutputFileDTO // 输出的文件
  photoUsage: number // 会员剩余的作图额度
  testType: string
}

export function selectNodeAndOrder(selectCommand: SelectNodeAndOrderCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_SELECT_NODE_AND_ORDER, 'post', {
    ...selectCommand,
    iosMiniapp: isIOS() && inMiniprograme.value
  })
}

export interface PhotoEnhanceHomeResponse {
  total: number
  task: Array<PhotoEnhanceTaskDTO>
  isMember: boolean // 是否是会员
  capability: number // 还剩几次免费生成机会
}

export function getPhotoEnhanceHome() {
  return netClient.request<CommonResponse<PhotoEnhanceHomeResponse>>(PORTRAIT_HOME, 'get', {})
}

export interface PhotoPayByMissbeanCommand {
  taskId: string
}

export function photoPayByMissbean(payCommand: PhotoPayByMissbeanCommand) {
  return netClient.request<CommonResponse<boolean>>(PORTRAIT_PAY_BY_COIN, 'post', payCommand)
}

export interface PhotoPayByCreditCommand {
  taskId: string
}

export function photoPayByCredit(payCommand: PhotoPayByCreditCommand) {
  return netClient.request<CommonResponse<boolean>>(PORTRAIT_PAY_BY_MEMBER, 'post', payCommand)
}

export interface PhotoEnhanceTaskDetailResponse {
  task: PhotoEnhanceTaskDTO
  outputFile: PhotoOutputFileDTO // 已支付返回
  altarAvatar: FileDTO
  lifeAvatar: FileDTO
}
//Path Param： taskId - string
export function getPhotoEnhanceTaskDetail(taskId: string) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDetailResponse>>(
    PORTRAIT_TASK_DETAIL.replace('{taskId}', taskId),
    'get',
    {}
  )
}
/**
 * params:
page: number; // 页码
pageSize: number; // 每页个数 
 */
export interface ListPhotoEnhanceTaskResponse {
  page: number
  pageSize: number
  items: Array<PhotoEnhanceTaskDTO>
  total: number
}

export function listPhotoEnhanceTask(page: number, pageSize: number) {
  return netClient.request<CommonResponse<ListPhotoEnhanceTaskResponse>>(PORTRAIT_TASKS_ME, 'get', { page, pageSize })
}

//DELETE /v1/photo/enhance/task/{id}
export function deletePhotoEnhanceTask(id: string) {
  return netClient.request<CommonResponse<any>>(PORTRAIT_TASK_DELETE.replace('{id}', id), 'delete', {})
}

// request
export interface RetryPhotoEnhanceNodeCommand {
  taskId: string
}

// response PhotoEnhanceNodeDTO

export function retryPhotoEnhanceNode(retryCommand: RetryPhotoEnhanceNodeCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_TASK_RETRY, 'post', retryCommand)
}

export interface PhotoShare2BonusCommand {
  taskId: string
}

// response type
// number 奖励了几个思念豆，弹出提示（同选择渠道后弹出的弹窗）
export function photoShare2Bonus(shareCommand: PhotoShare2BonusCommand) {
  return netClient.request<CommonResponse<number>>(PORTRAIT_TASK_SHARE, 'post', shareCommand)
}

/**
 * GET /v1/photo/enhance/tasks
Params:
id - string
pageSize - number
sort - string
top  - bool (是否置顶)
 */
export interface ListOpenPhotoEnhanceCommand {
  id: string
  pageSize: number
  sort: string
  top: boolean
}
export interface ListOpenPhotoEnhanceResponse {
  id: string
  tasks: Array<PhotoEnhanceTaskDTO>
  total: number
}
export function getOpenTask(listCommand: ListOpenPhotoEnhanceCommand) {
  return netClient.request<CommonResponse<ListOpenPhotoEnhanceResponse>>(PORTRAIT_TASKS_OPEN, 'get', listCommand)
}

// request
export interface DetectFacesCommand {
  fileId: string
}

export function detectFaces(detectCommand: DetectFacesCommand) {
  return netClient.request<CommonResponse<UploadFileDetectFaceResponse>>(PORTRAIT_DETECT_FACES, 'post', detectCommand)
}

export interface SubmitPhotoEnhanceAdviceCommand {
  nodeId: string // 传当前正在展示的nodeId
  advice: string
}

export function submitPhotoEnhanceAdvice(adviceCommand: SubmitPhotoEnhanceAdviceCommand) {
  return netClient.request<CommonResponse<boolean>>(PORTRAIT_ADVICE, 'post', adviceCommand)
}

export interface buttonInfo {
  id?: string | null
  title: string
  price: number | null
  actionKey: string
  style: number
}

export interface SetPhotoTransparentCommand {
  taskId: string
  sourceFileId: string
}

// response PhotoEnhanceTaskDTO
export function setPhotoTransparent(transparentCommand: SetPhotoTransparentCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_SET_TRANSPARENT, 'post', transparentCommand)
}

export interface MergeBackgroundCommand {
  nodeId: string
  isGradient: boolean
  startColor: string
  endColor: string
}

// response PhotoEnhanceNodeDTO
export function mergeBackground(mergeCommand: MergeBackgroundCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceNodeDTO>>(PORTRAIT_MERGE_BACKGROUND, 'post', mergeCommand)
}
// PUT /v1/photo/enhance:capture?lastNodeId={nodeId}
// Body: file : MultipartFile， response PhotoEnhanceNodeDTO

export interface CaptureCommand {
  nodeId: string
  file: Blob
  fileName: string
}

export function capture(captureCommand: CaptureCommand) {
  const formData = new FormData()
  if (captureCommand.file != null) {
    formData.append('file', captureCommand.file, captureCommand.fileName)
  }
  return netClient.formPut<CommonResponse<PhotoEnhanceNodeDTO>>(
    PORTRAIT_CAPTURE.replace('{nodeId}', captureCommand.nodeId),
    formData
  )
}

export interface MakeImageHdCommand {
  taskId: string
  fileId: string
  planId: string // 基础高清或者Prompt生成高清
}

export function makeImageHd(hdCommand: MakeImageHdCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_MAKE_HD, 'post', hdCommand)
}

export interface ColorizeImageCommand {
  taskId: string
  fileId: string
  planId: string
}

export function colorizeImage(colorizeCommand: ColorizeImageCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_COLORIZE, 'post', colorizeCommand)
}

export interface AdjustImageGestureCommand {
  taskId: string
  fileId: string
  planId: string
}

export function adjustImageGesture(adjustCommand: AdjustImageGestureCommand) {
  return netClient.request<CommonResponse<PhotoEnhanceTaskDTO>>(PORTRAIT_ADJUST_GESTURE, 'post', adjustCommand)
}

/**
 * "general" - 一键修图
"beautify" - 换装换发型
"transparent" - 人体抠图
"hd" - 高清
"gesture" - 体态调整
"color" - 上色
 */
// GET /v1/photo/enhance/plan/{scene}

export interface EnhancePlansSceneCommand {
  scene: string
}

export function enhancePlansScene(enhanceCommand: EnhancePlansSceneCommand) {
  return netClient.request<CommonResponse<Array<PhotoEnhancePlanDTO>>>(
    PORTRAIT_ENHANCE_PLANS_SCENE.replace('{scene}', enhanceCommand.scene),
    'get',
    {}
  )
}

export interface PhotoLayoutCommand {
  nodeId: string
  /**
   * 1 - 1寸
   * 1x - 大一寸
   * 2 - 2寸
   */
  photoSize: string
}

export interface PhotoLayoutResponse {
  photoLayouts: Array<PhotoLayout>
}

export interface PhotoLayout {
  url: string // url
  spec: string // 排版说明，i.e., 大1寸照片在6寸(4R)相纸的排版
  size: string // 6  （意思为6寸相纸，取值可能是5、6、A3、A4、12、16）
}

export function photoLayout(command: PhotoLayoutCommand) {
  return netClient.request<CommonResponse<PhotoLayoutResponse>>(PORTRAIT_ENHANCE_LAYOUT, 'post', command)
}

export interface SelectNodeCommand {
  nodeId: string // nodeId
}
export function selectNode(selectCommand: SelectNodeCommand) {
  return netClient.request<CommonResponse<string>>(PORTRAIT_SELECT_NODE, 'post', selectCommand)
}
